import type { DependencyList, MutableRefObject } from 'react';
import { useEffect, useRef } from 'react';
import { useDebounce } from 'react-use';

interface EstateStatusAutosaveSavedArgs<TValue, TSavedValue> {
  savedValue: TSavedValue;
  savedSyncedValue: TValue;
  savedChangeToken: string;
  submittedValue: TValue;
  submittedSerializedValue: string;
  currentValue: TValue;
  isMounted: boolean;
}

interface UseEstateStatusAutosaveArgs<TValue, TSavedValue> {
  value: TValue;
  syncedValue: TValue;
  changeToken: string;
  isDirty: boolean;
  isMutationSaving: boolean;
  debounceMs: number;
  debounceDependencies: DependencyList;
  canSaveValue: (value: TValue, syncedValue: TValue) => boolean;
  serializeValue: (value: TValue) => string;
  saveValue: (args: { changeToken: string; value: TValue }) => Promise<TSavedValue | null | undefined>;
  savedValueToSyncedValue: (savedValue: TSavedValue) => TValue;
  savedValueToChangeToken: (savedValue: TSavedValue) => string;
  onSavedValue?: (args: EstateStatusAutosaveSavedArgs<TValue, TSavedValue>) => void;
}

interface UseEstateStatusAutosaveReturn<TValue, TSavedValue> {
  isDirtyRef: MutableRefObject<boolean>;
  currentValueRef: MutableRefObject<TValue>;
  syncedValueRef: MutableRefObject<TValue>;
  changeTokenRef: MutableRefObject<string>;
  canAutoSave: boolean;
  markDirty: (nextValue?: TValue) => void;
  saveCurrent: () => void;
  saveCurrentAsync: () => Promise<TSavedValue | null | undefined>;
}

const maxSaveCurrentAsyncRetries = 5;

export function useEstateStatusAutosave<TValue, TSavedValue>({
  value,
  syncedValue,
  changeToken,
  isDirty,
  isMutationSaving,
  debounceMs,
  debounceDependencies,
  canSaveValue,
  serializeValue,
  saveValue,
  savedValueToSyncedValue,
  savedValueToChangeToken,
  onSavedValue,
}: UseEstateStatusAutosaveArgs<TValue, TSavedValue>): UseEstateStatusAutosaveReturn<TValue, TSavedValue> {
  const currentValueRef = useRef(value);
  const syncedValueRef = useRef(syncedValue);
  const changeTokenRef = useRef(changeToken);
  const isDirtyRef = useRef(false);
  const canAutoSaveRef = useRef(false);
  const isSavingRef = useRef(false);
  const activeSavePromiseRef = useRef<Promise<TSavedValue | null | undefined> | null>(null);
  const hasQueuedSaveAfterCurrentMutationRef = useRef(false);
  const isMountedRef = useRef(true);

  const canAutoSave = canSaveValue(value, syncedValue);

  currentValueRef.current = value;
  syncedValueRef.current = syncedValue;
  changeTokenRef.current = changeToken;
  canAutoSaveRef.current = canAutoSave;
  isSavingRef.current = isMutationSaving || activeSavePromiseRef.current !== null;

  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  const markDirty = (nextValue?: TValue) => {
    isDirtyRef.current = true;

    if (activeSavePromiseRef.current) {
      hasQueuedSaveAfterCurrentMutationRef.current = true;
    }

    if (nextValue !== undefined) {
      currentValueRef.current = nextValue;
      canAutoSaveRef.current = canSaveValue(nextValue, syncedValueRef.current);
    }
  };

  const startSaveCurrent = () => {
    const currentValue = currentValueRef.current;

    if (!canAutoSaveRef.current && !canSaveValue(currentValue, syncedValueRef.current)) return null;

    if (isSavingRef.current) {
      hasQueuedSaveAfterCurrentMutationRef.current = true;
      return activeSavePromiseRef.current;
    }

    const submittedValue = currentValue;
    const submittedSerializedValue = serializeValue(submittedValue);
    const savePromise = saveValue({
      changeToken: changeTokenRef.current,
      value: submittedValue,
    });
    const handledSavePromise = savePromise
      .then((savedValue) => {
        if (!savedValue) return savedValue;

        const savedSyncedValue = savedValueToSyncedValue(savedValue);
        const savedChangeToken = savedValueToChangeToken(savedValue);

        changeTokenRef.current = savedChangeToken;
        syncedValueRef.current = savedSyncedValue;
        canAutoSaveRef.current = canSaveValue(currentValueRef.current, savedSyncedValue);

        if (serializeValue(currentValueRef.current) === submittedSerializedValue) {
          isDirtyRef.current = false;
        }

        onSavedValue?.({
          savedValue,
          savedSyncedValue,
          savedChangeToken,
          submittedValue,
          submittedSerializedValue,
          currentValue: currentValueRef.current,
          isMounted: isMountedRef.current,
        });

        return savedValue;
      })
      .finally(() => {
        if (activeSavePromiseRef.current === handledSavePromise) {
          activeSavePromiseRef.current = null;
          isSavingRef.current = false;
        }

        if (!hasQueuedSaveAfterCurrentMutationRef.current) return;

        hasQueuedSaveAfterCurrentMutationRef.current = false;
        window.setTimeout(() => {
          saveCurrentRef.current();
        }, 0);
      });

    activeSavePromiseRef.current = handledSavePromise;
    isSavingRef.current = true;

    // Mutation state drives user-facing errors; keep debounce and close-time
    // saves from surfacing unhandled promise rejections.
    void handledSavePromise.catch(() => {});

    return handledSavePromise;
  };

  const saveCurrent = () => {
    void startSaveCurrent();
  };

  const saveCurrentAsync = async () => {
    let savedValue = await startSaveCurrent();

    for (
      let attempt = 0;
      attempt < maxSaveCurrentAsyncRetries && canSaveValue(currentValueRef.current, syncedValueRef.current);
      attempt += 1
    ) {
      await new Promise((resolve) => window.setTimeout(resolve, 0));
      savedValue = await startSaveCurrent();

      if (!savedValue && !activeSavePromiseRef.current) break;
    }

    return savedValue;
  };

  const saveCurrentRef = useRef(saveCurrent);
  saveCurrentRef.current = saveCurrent;

  useEffect(() => {
    return () => {
      saveCurrentRef.current();
      isMountedRef.current = false;
    };
  }, []);

  useDebounce(
    () => {
      saveCurrent();
    },
    debounceMs,
    [canAutoSave, ...debounceDependencies]
  );

  return {
    isDirtyRef,
    currentValueRef,
    syncedValueRef,
    changeTokenRef,
    canAutoSave,
    markDirty,
    saveCurrent,
    saveCurrentAsync,
  };
}
